import type { AxiosError } from 'axios';
import apiV2 from '../api/v2';
import { toDmError } from './dmErrors';

export interface DmNotes {
    notes: string;
}

export interface LiveDmNotes {
    notes: string;
    changed: boolean;
}

export interface DmNotesReadError extends Error {
    status: number | null;
}

export interface PatchNotesBody {
    text: string;
}

// DM can hang; the live query makes one attempt, so past this the editor falls back to the synced copy.
const LIVE_NOTES_TIMEOUT_MS = 12_000;

export async function getDmNotes(patientId: string): Promise<DmNotes> {
    try {
        const response = await apiV2.get<{ success: boolean; data: DmNotes }>(
            `/patients/${patientId}/dm/notes`
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

function toDmNotesReadError(err: unknown): DmNotesReadError {
    const status = (err as AxiosError).response?.status ?? null;
    return Object.assign(toDmError(err), { status });
}

function isLiveDmNotes(data: unknown): data is LiveDmNotes {
    if (typeof data !== 'object' || data === null) return false;
    const { notes, changed } = data as Record<string, unknown>;
    return typeof notes === 'string' && typeof changed === 'boolean';
}

export async function getLiveDmNotes(
    patientId: string,
    signal?: AbortSignal
): Promise<LiveDmNotes> {
    let data: unknown;
    try {
        const response = await apiV2.get<{ success: boolean; data: unknown }>(
            `/patients/${patientId}/dm/notes/live`,
            { signal, timeout: LIVE_NOTES_TIMEOUT_MS }
        );
        data = response.data.data;
    } catch (err) {
        throw toDmNotesReadError(err);
    }
    if (!isLiveDmNotes(data)) {
        throw Object.assign(new Error('Unexpected live DM notes response.'), { status: null });
    }
    return data;
}

export async function patchDmNotes(patientId: string, text: string): Promise<{ notes: string }> {
    try {
        const response = await apiV2.patch<{ success: boolean; data: { notes: string } }>(
            `/patients/${patientId}/dm/notes`,
            { text }
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}
